import React, { useRef } from "react";

const MAX_FILES = 8;

export default function StepPhotos({ form, handlePhotoUpload, removePhoto, onNext, onSkip }) {
  const uploadRef       = useRef(null);
  const captureRef      = useRef(null);
  const videoCaptureRef = useRef(null);
  const videoUploadRef  = useRef(null);

  const tapZoneStyle = {
    border: "2px dashed rgba(255, 138, 0,0.3)",
    borderRadius: "16px",
    padding: "24px 16px",
    minHeight: "110px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    cursor: "pointer",
    background: "rgba(255, 138, 0,0.03)",
  };

  return (
    <div>
      <style>{`
        .step-photos-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 20px;
        }
        @media (min-width: 640px) {
          .step-photos-grid { grid-template-columns: repeat(4, 1fr); }
        }
      `}</style>

      <h3 style={{ margin: "0 0 8px", fontWeight: 700, fontSize: "1.1rem" }}>Add a photo or video</h3>
      <p style={{ color: "var(--muted, #6b7280)", fontSize: "0.93rem", marginBottom: "24px" }}>
        Photos and videos help tradies give you a more accurate price — even one makes a big difference.
      </p>

      {form.uploadedPhotos.length < MAX_FILES && (
        <div className="step-photos-grid">
          {/* Upload a photo */}
          <div
            style={tapZoneStyle}
            onClick={() => uploadRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && uploadRef.current?.click()}
          >
            <span style={{ fontSize: "28px", lineHeight: 1 }}>🖼️</span>
            <span style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--text, #1C1C1E)", textAlign: "center" }}>Upload a photo</span>
            <span style={{ color: "var(--muted, #6b7280)", fontSize: "0.78rem", textAlign: "center" }}>From your library</span>
          </div>

          {/* Take a photo */}
          <div
            style={tapZoneStyle}
            onClick={() => captureRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && captureRef.current?.click()}
          >
            <span style={{ fontSize: "28px", lineHeight: 1 }}>📸</span>
            <span style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--text, #1C1C1E)", textAlign: "center" }}>Take a photo</span>
            <span style={{ color: "var(--muted, #6b7280)", fontSize: "0.78rem", textAlign: "center" }}>Open your camera</span>
          </div>

          {/* Record a video */}
          <div
            style={tapZoneStyle}
            onClick={() => videoCaptureRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && videoCaptureRef.current?.click()}
          >
            <span style={{ fontSize: "28px", lineHeight: 1 }}>🎥</span>
            <span style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--text, #1C1C1E)", textAlign: "center" }}>Record a video</span>
            <span style={{ color: "var(--muted, #6b7280)", fontSize: "0.78rem", textAlign: "center" }}>Walk the tradie through the job</span>
          </div>

          {/* Upload a video */}
          <div
            style={tapZoneStyle}
            onClick={() => videoUploadRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && videoUploadRef.current?.click()}
          >
            <span style={{ fontSize: "28px", lineHeight: 1 }}>📁</span>
            <span style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--text, #1C1C1E)", textAlign: "center" }}>Upload a video</span>
            <span style={{ color: "var(--muted, #6b7280)", fontSize: "0.78rem", textAlign: "center" }}>From your camera roll</span>
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      <input ref={uploadRef}       type="file" accept="image/*"  multiple           style={{ display: "none" }} onChange={handlePhotoUpload} />
      <input ref={captureRef}      type="file" accept="image/*"  capture="environment" style={{ display: "none" }} onChange={handlePhotoUpload} />
      <input ref={videoCaptureRef} type="file" accept="video/*"  capture="environment" style={{ display: "none" }} onChange={handlePhotoUpload} />
      <input ref={videoUploadRef}  type="file" accept="video/*"                     style={{ display: "none" }} onChange={handlePhotoUpload} />

      {/* Media thumbnails */}
      {form.uploadedPhotos.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          {form.uploadedPhotos.map((photo, index) => (
            <div key={`${photo.name}-${index}`} style={{ position: "relative", borderRadius: "12px", overflow: "hidden" }}>
              <button
                type="button"
                onClick={() => removePhoto(index)}
                aria-label={`Remove ${photo.name}`}
                style={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  zIndex: 2,
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(0,0,0,0.65)",
                  color: "#fff",
                  fontSize: 16,
                  lineHeight: "26px",
                  textAlign: "center",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                ×
              </button>
              {photo.type?.startsWith("video/") ? (
                <div style={{ position: "relative", width: "100%", aspectRatio: "1", background: "#000" }}>
                  <video
                    src={photo.dataUrl}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    muted
                    playsInline
                  />
                  <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "rgba(0,0,0,0.25)",
                  }}>
                    <div style={{
                      width: "30px", height: "30px", borderRadius: "50%",
                      background: "rgba(255,255,255,0.9)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="#1C1C1E"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    </div>
                  </div>
                </div>
              ) : (
                <img
                  src={photo.dataUrl}
                  alt={photo.name}
                  style={{ width: "100%", aspectRatio: "1", objectFit: "cover", display: "block" }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={onNext}
        style={{
          background: "var(--primary)",
          color: "#fff",
          border: "none",
          borderRadius: "12px",
          padding: "18px 32px",
          fontSize: "1rem",
          fontWeight: 700,
          cursor: "pointer",
          width: "100%",
          marginBottom: "12px",
        }}
      >
        Next →
      </button>

      <div style={{ textAlign: "center" }}>
        <button
          type="button"
          onClick={onSkip || onNext}
          style={{
            background: "none",
            border: "none",
            color: "var(--muted, #6b7280)",
            fontSize: "0.9rem",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Skip for now →
        </button>
      </div>
    </div>
  );
}
